import { ScrollableCards } from "../ScrollableCards";
import { handleCardCtaClick } from "../../MobileApp/webviewBridge";
import type { Product, ProductResult } from "./validate";
export type { Product, ProductResult } from "./validate";

export function formatPrice(price: number, priceMax?: number): string {
  const fmt = (n: number) => {
    const fixed = Number.isInteger(n) ? n.toString() : n.toFixed(2);
    return `$${fixed}`;
  };
  if (priceMax && priceMax !== price) return `${fmt(price)}--${fmt(priceMax)}`;
  return fmt(price);
}

function Stars({ rating }: { rating: number }) {
  const rounded = Math.round(rating);
  return (
    <span className="lk-product-card-stars" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          className={n <= rounded ? "lk-star lk-star--on" : "lk-star"}
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2l2.95 6.18 6.8.78-5.05 4.6 1.38 6.66L12 17.6l-6.08 3.42 1.38-6.66-5.05-4.6 6.8-.78z" />
        </svg>
      ))}
    </span>
  );
}

interface ProductCardProps {
  product: Product;
}

function ProductCard({ product }: ProductCardProps) {
  return (
    <a
      className="lk-product-card"
      href={product.productUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => handleCardCtaClick(e, product.productUrl)}
    >
      <div
        className="lk-product-card-image"
        style={product.imageUrl ? { backgroundImage: `url(${product.imageUrl})` } : undefined}
        role="img"
        aria-label={product.title}
      />
      <div className="lk-product-card-body">
        <div className="lk-product-card-top">
          <div className="lk-product-card-brand">{product.brand.toUpperCase()}</div>
          <div className="lk-product-card-name">{product.title}</div>
        </div>
        {product.ratingCount != null ? (
          <div
            className="lk-product-card-rating"
            aria-label={product.ratingCount ? `Rated ${product.rating} of 5` : "No reviews"}
          >
            <Stars rating={product.rating ?? 0} />
            <span className="lk-product-card-rating-count">
              {product.ratingCount
                ? `${(product.rating ?? 0).toFixed(1)} (${product.ratingCount})`
                : "No reviews yet"}
            </span>
          </div>
        ) : null}
        {product.description && (
          <div className="lk-product-card-desc">{product.description}</div>
        )}
        <div className="lk-product-card-bottom">
          <span className="lk-product-card-price">
            {formatPrice(product.price, product.priceMax)}
          </span>
          <span className="lk-product-card-cta">View</span>
        </div>
      </div>
    </a>
  );
}

interface ProductCardsProps {
  products: Product[];
}

export function ProductCards({ products }: ProductCardsProps) {
  if (products.length === 0) return null;
  if (products.length === 1) return <ProductCard product={products[0]} />;
  return (
    <ScrollableCards className="lk-product-cards">
      {products.map((product, i) => (
        <ProductCard key={`${product.title}-${i}`} product={product} />
      ))}
    </ScrollableCards>
  );
}
